
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewEncapsulation, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UtilityRatesDisplayComponent } from '../utility-rates-display/utility-rates-display.component';
import { ConfirmationModalComponent, ConfirmationModalData } from '../confirmation-modal';
import { DemandRatesSavePayload, EnergyRatesSavePayload, UtilityRateScheduleVersion, UtilitySchedule, ScheduleMetadata } from '../../../setup/utility/interfaces/utility.interface';
import { UtilityService } from '../../../setup/utility/services/utility.service';

@Component({
  selector: 'app-utility-rates-modal',
  standalone: true,
  imports: [FormsModule, UtilityRatesDisplayComponent, ConfirmationModalComponent],
  templateUrl: './utility-rates-modal.component.html',
  styleUrl: './utility-rates-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class UtilityRatesModalComponent implements OnChanges, AfterViewInit, OnDestroy {
  private utilityService = inject(UtilityService);
  private elementRef = inject(ElementRef);
  private modalElement: HTMLElement | null = null;
  private originalParent: Node | null = null;

  @Input() isVisible = false;
  @Input() utilitySchedule: UtilitySchedule | null = null;
  @Input() utilityId: number | null = null;
  @Input() siteId: number | null = null;
  @Input() organizationId: number | null = null;
  @Input() readOnly = false;
  /** When set, select this schedule version row on open (e.g. billing period's resolved version). */
  @Input() initialVersionScheduleId: number | null = null;
  /** Fallback: pick the version effective on this date (YYYY-MM-DD) when initial id is unset or missing. */
  @Input() versionAsOfDate: string | null = null;

  @Output() close = new EventEmitter<void>();

  @ViewChild('modalContainer', { static: false }) modalContainer?: ElementRef<HTMLElement>;

  private isLoadingRates = signal(true);
  protected getIsLoadingRates = () => this.isLoadingRates();
  private isSavingDemandRates = signal(false);
  protected getIsSavingDemandRates = () => this.isSavingDemandRates();
  private isSavingEnergyRates = signal(false);
  protected getIsSavingEnergyRates = () => this.isSavingEnergyRates();
  private isSavingServiceCharges = signal(false);
  protected getIsSavingServiceCharges = () => this.isSavingServiceCharges();
  private isSavingOtherCharges = signal(false);
  protected getIsSavingOtherCharges = () => this.isSavingOtherCharges();
  private isSavingNotes = signal(false);
  protected getIsSavingNotes = () => this.isSavingNotes();
  private scheduleMetadata = signal<ScheduleMetadata | null>(null);
  protected getScheduleMetadata = () => this.scheduleMetadata();
  private rateVersions = signal<UtilityRateScheduleVersion[]>([]);
  protected getRateVersions = () => this.rateVersions();
  private selectedVersionScheduleId = signal<number | null>(null);
  protected getSelectedVersionScheduleId = () => this.selectedVersionScheduleId();
  /** Persisted ``utility_schedule.id`` for the selected version (not catalog-only rows). */
  private resolvedScheduleId(): number | null {
    const id = this.selectedVersionScheduleId() ?? this.utilitySchedule?.id;
    return id ?? null;
  }

  protected getRateVersionOptions = () => {
    const versions = this.rateVersions();
    if (!versions.length) {
      return [{
        scheduleId: this.utilitySchedule?.id ?? null,
        label: 'Current (latest)'
      }];
    }
    return versions.map((version) => ({
      scheduleId: version.utility_schedule_id,
      label: this.formatRateVersionLabel(version)
    }));
  };

  /** When saving with toDelete, we show confirmation first and store the pending payload here. */
  private pendingDemandSavePayload: DemandRatesSavePayload | null = null;
  private pendingEnergySavePayload: EnergyRatesSavePayload | null = null;
  showDeleteRatesConfirmationModal = signal(false);
  deleteRatesModalData = signal<ConfirmationModalData | null>(null);

  protected getDemandRates() {
    return this.utilityService.getDemandChargeRatesSignal()();
  }

  protected getEnergyRates() {
    return this.utilityService.getEnergyRatesSignal()();
  }

  protected formatScheduleDisplayName(schedule: UtilitySchedule | null | undefined): string {
    if (!schedule) return '-';

    const hasName = schedule.name != null && schedule.name.trim() !== '';
    const hasDescription = schedule.description != null && schedule.description.trim() !== '';
    const hasUseType = schedule.use_type != null && schedule.use_type.trim() !== '';
    const hasExternalId = schedule.external_utility_schedule_id != null && schedule.external_utility_schedule_id.trim() !== '';

    if (hasDescription && hasName) {
      return `${schedule.description} (${schedule.name})`;
    } else if (hasDescription) {
      return schedule.description!;
    } else if (hasName) {
      return schedule.name!;
    } else if (hasUseType) {
      return schedule.use_type!;
    } else if (hasExternalId) {
      return `Rate ID: ${schedule.external_utility_schedule_id}`;
    } else {
      return '-';
    }
  }

  protected onBackdropClick(event: Event) {
    // Close modal if clicking on the backdrop (not the modal content)
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  protected onClose() {
    this.close.emit();
    this.utilityService.clearRates();
  }

  ngAfterViewInit() {
    // If modal is already visible on init, move it to body
    if (this.isVisible) {
      setTimeout(() => this.moveModalToBody(), 0);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Load rates when modal becomes visible and we have the necessary data
    if (changes['isVisible']?.currentValue && this.isVisible && this.utilitySchedule && this.utilityId) {
      this.loadRateVersionsAndRates();
    }

    // Move modal to body when visible, restore when hidden
    if (changes['isVisible']) {
      if (this.isVisible) {
        // Use setTimeout to ensure DOM is updated and ViewChild is available
        setTimeout(() => this.moveModalToBody(), 0);
      } else {
        this.restoreModalFromBody();
      }
    }
  }

  ngOnDestroy() {
    this.restoreModalFromBody();
  }

  private moveModalToBody() {
    if (!this.modalContainer || !document.body) return;

    const element = this.modalContainer.nativeElement;
    if (!element || element.parentNode === document.body) return;

    // Store original parent
    this.originalParent = element.parentNode;
    this.modalElement = element;

    // Move to body
    document.body.appendChild(element);
  }

  private restoreModalFromBody() {
    if (!this.modalElement || !this.originalParent) return;

    if (this.modalElement.parentNode === document.body) {
      // Move back to original location
      this.originalParent.appendChild(this.modalElement);
    }

    this.modalElement = null;
    this.originalParent = null;
  }

  private loadRates() {
    if (!this.utilitySchedule || !this.utilityId) {
      return;
    }

    this.isLoadingRates.set(true);

    const selectedScheduleId = this.resolvedScheduleId();
    if (selectedScheduleId == null) {
      this.isLoadingRates.set(false);
      return;
    }
    const alreadyFetched = this.utilityService.hasRatesFetchedForSchedule(
      this.utilityId,
      selectedScheduleId
    );
    if (alreadyFetched) {
      this.isLoadingRates.set(false);
      return;
    }

    this.scheduleMetadata.set(null);
    this.utilityService.clearRates();
    this.utilityService.getUtilityScheduleDetail(this.utilityId, selectedScheduleId).subscribe({
      next: (detail) => {
        this.scheduleMetadata.set(detail.schedule_metadata);
        this.isLoadingRates.set(false);
      },
      error: (error: any) => {
        console.error('Error loading rates:', error);
        this.isLoadingRates.set(false);
      }
    });
  }

  private loadRateVersionsAndRates() {
    if (!this.utilitySchedule || !this.utilityId) {
      return;
    }
    this.isLoadingRates.set(true);
    const anchorScheduleId = this.utilitySchedule.id;
    if (anchorScheduleId == null) {
      this.isLoadingRates.set(false);
      return;
    }
    this.utilityService
      .getUtilityScheduleRateVersions(this.utilityId, anchorScheduleId)
      .subscribe({
        next: (versions) => {
          this.rateVersions.set(versions ?? []);
          this.selectedVersionScheduleId.set(
            this.pickInitialVersionScheduleId(versions ?? [])
          );
          this.loadRates();
        },
        error: (error) => {
          console.error('Error loading rate versions:', error);
          this.rateVersions.set([]);
          this.selectedVersionScheduleId.set(this.utilitySchedule?.id ?? null);
          this.loadRates();
        },
      });
  }

  private pickInitialVersionScheduleId(
    versions: UtilityRateScheduleVersion[]
  ): number | null {
    if (this.initialVersionScheduleId != null) {
      const match = versions.find(
        (v) => v.utility_schedule_id === this.initialVersionScheduleId
      );
      if (match) {
        return match.utility_schedule_id;
      }
    }
    if (this.versionAsOfDate) {
      const asOf = this.versionAsOfDate.slice(0, 10);
      const covering = versions.find((v) => {
        const start = v.effective_date.slice(0, 10);
        const end = v.effective_end_date?.slice(0, 10);
        return start <= asOf && (!end || end >= asOf);
      });
      if (covering) {
        return covering.utility_schedule_id;
      }
    }
    const currentVersion = versions.find((v) => v.is_current);
    return currentVersion?.utility_schedule_id ?? this.utilitySchedule?.id ?? null;
  }

  protected formatRateVersionLabel(version: UtilityRateScheduleVersion): string {
    const start = this.formatRateVersionDate(version.effective_date);
    if (version.is_current) {
      return `${start} - Present`;
    }
    const end = version.effective_end_date
      ? this.formatRateVersionDate(version.effective_end_date)
      : 'Present';
    return `${start} - ${end}`;
  }

  private formatRateVersionDate(dateValue: string): string {
    // Parse YYYY-MM-DD safely (without timezone shift) for user-friendly labels.
    const isoDateOnly = dateValue.split('T')[0];
    const parts = isoDateOnly.split('-');
    if (parts.length !== 3) {
      return dateValue;
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return dateValue;
    }

    const dt = new Date(Date.UTC(year, month - 1, day));
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(dt);
  }

  protected onRateVersionChange(selectedScheduleId: number | null) {
    if (this.selectedVersionScheduleId() === selectedScheduleId) {
      return;
    }
    this.selectedVersionScheduleId.set(selectedScheduleId);
    this.loadRates();
  }

  protected onSaveDemandRates(payload: DemandRatesSavePayload) {
    if (!this.utilityId || !this.utilitySchedule) {
      return;
    }
    if (payload.toDelete?.length > 0) {
      this.pendingDemandSavePayload = payload;
      const scheduleName = this.formatScheduleDisplayName(this.utilitySchedule);
      const count = payload.toDelete.length;
      this.deleteRatesModalData.set({
        title: 'Delete demand rates?',
        message: `Are you sure you want to delete ${count} row(s) from this schedule?`,
        itemName: scheduleName,
        confirmButtonText: 'Delete',
        confirmButtonClass: 'btn-danger',
        iconClass: 'bi bi-trash text-danger',
      });
      this.showDeleteRatesConfirmationModal.set(true);
      return;
    }
    this.runDemandRatesSave(payload);
  }

  protected onSaveEnergyRates(payload: EnergyRatesSavePayload) {
    if (!this.utilityId || !this.utilitySchedule) {
      return;
    }
    if (payload.toDelete?.length > 0) {
      this.pendingEnergySavePayload = payload;
      const scheduleName = this.formatScheduleDisplayName(this.utilitySchedule);
      const count = payload.toDelete.length;
      this.deleteRatesModalData.set({
        title: 'Delete energy rates?',
        message: `Are you sure you want to delete ${count} row(s) from this schedule?`,
        itemName: scheduleName,
        confirmButtonText: 'Delete',
        confirmButtonClass: 'btn-danger',
        iconClass: 'bi bi-trash text-danger',
      });
      this.showDeleteRatesConfirmationModal.set(true);
      return;
    }
    this.runEnergyRatesSave(payload);
  }

  protected onDeleteRatesConfirm() {
    if (this.pendingDemandSavePayload) {
      const payload = this.pendingDemandSavePayload;
      this.pendingDemandSavePayload = null;
      this.showDeleteRatesConfirmationModal.set(false);
      this.deleteRatesModalData.set(null);
      this.runDemandRatesSave(payload);
    } else if (this.pendingEnergySavePayload) {
      const payload = this.pendingEnergySavePayload;
      this.pendingEnergySavePayload = null;
      this.showDeleteRatesConfirmationModal.set(false);
      this.deleteRatesModalData.set(null);
      this.runEnergyRatesSave(payload);
    }
  }

  protected onDeleteRatesCancel() {
    this.pendingDemandSavePayload = null;
    this.pendingEnergySavePayload = null;
    this.showDeleteRatesConfirmationModal.set(false);
    this.deleteRatesModalData.set(null);
  }

  private runDemandRatesSave(payload: DemandRatesSavePayload) {
    const utilityId = this.utilityId!;
    const scheduleId = this.resolvedScheduleId();
    if (scheduleId == null) {
      return;
    }
    const deleteRequests = (payload.toDelete ?? []).map((id) =>
      this.utilityService.deleteDemandRate(utilityId, scheduleId, id)
    );
    const createRequests = payload.toCreate.map((rate) =>
      this.utilityService.createDemandRate(utilityId, scheduleId, rate)
    );
    const updateRequests = payload.toUpdate.map((rate) =>
      this.utilityService.updateDemandRate(utilityId, scheduleId, rate.id, rate)
    );
    const requests = [...deleteRequests, ...createRequests, ...updateRequests];
    if (requests.length === 0) {
      return;
    }
    this.isSavingDemandRates.set(true);
    forkJoin(requests).pipe(
      switchMap(() => this.utilityService.getUtilityScheduleDetail(utilityId, scheduleId)),
      catchError((err) => {
        console.error('Error saving demand rates:', err);
        this.isSavingDemandRates.set(false);
        return of(null);
      })
    ).subscribe({
      next: (detail) => {
        if (detail) {
          this.scheduleMetadata.set(detail.schedule_metadata);
        }
        this.isSavingDemandRates.set(false);
      },
      error: () => {
        this.isSavingDemandRates.set(false);
      }
    });
  }

  private runEnergyRatesSave(payload: EnergyRatesSavePayload) {
    const utilityId = this.utilityId!;
    const scheduleId = this.resolvedScheduleId();
    if (scheduleId == null) {
      return;
    }
    const deleteRequests = (payload.toDelete ?? []).map((id) =>
      this.utilityService.deleteEnergyRate(utilityId, scheduleId, id)
    );
    const createRequests = payload.toCreate.map((rate) =>
      this.utilityService.createEnergyRate(utilityId, scheduleId, rate)
    );
    const updateRequests = payload.toUpdate.map((rate) =>
      this.utilityService.updateEnergyRate(utilityId, scheduleId, rate.id, rate)
    );
    const requests = [...deleteRequests, ...createRequests, ...updateRequests];
    if (requests.length === 0) {
      return;
    }
    this.isSavingEnergyRates.set(true);
    forkJoin(requests).pipe(
      switchMap(() => this.utilityService.getUtilityScheduleDetail(utilityId, scheduleId)),
      catchError((err) => {
        console.error('Error saving energy rates:', err);
        this.isSavingEnergyRates.set(false);
        return of(null);
      })
    ).subscribe({
      next: (detail) => {
        if (detail) {
          this.scheduleMetadata.set(detail.schedule_metadata);
        }
        this.isSavingEnergyRates.set(false);
      },
      error: () => {
        this.isSavingEnergyRates.set(false);
      }
    });
  }

  protected onSaveServiceCharges(metadata: ScheduleMetadata): void {
    if (!this.utilityId || !this.utilitySchedule) {
      return;
    }
    const scheduleId = this.resolvedScheduleId();
    if (scheduleId == null) {
      return;
    }
    this.isSavingServiceCharges.set(true);
    this.utilityService.patchScheduleMetadata(this.utilityId, scheduleId, metadata).pipe(
      catchError((err) => {
        console.error('Error saving service charges:', err);
        this.isSavingServiceCharges.set(false);
        return of(null);
      })
    ).subscribe({
      next: (updated) => {
        this.isSavingServiceCharges.set(false);
        if (updated) {
          this.scheduleMetadata.set(updated);
        }
      },
      error: () => {
        this.isSavingServiceCharges.set(false);
      }
    });
  }

  protected onSaveOtherCharges(metadata: ScheduleMetadata): void {
    if (!this.utilityId || !this.utilitySchedule) {
      return;
    }
    const scheduleId = this.resolvedScheduleId();
    if (scheduleId == null) {
      return;
    }
    this.isSavingOtherCharges.set(true);
    this.utilityService.patchScheduleMetadata(this.utilityId, scheduleId, metadata).pipe(
      catchError((err) => {
        console.error('Error saving other charges:', err);
        this.isSavingOtherCharges.set(false);
        return of(null);
      })
    ).subscribe({
      next: (updated) => {
        this.isSavingOtherCharges.set(false);
        if (updated) {
          this.scheduleMetadata.set(updated);
        }
      },
      error: () => {
        this.isSavingOtherCharges.set(false);
      }
    });
  }

  protected onSaveNotes(metadata: ScheduleMetadata): void {
    if (!this.utilityId || !this.utilitySchedule) {
      return;
    }
    const scheduleId = this.resolvedScheduleId();
    if (scheduleId == null) {
      return;
    }
    this.isSavingNotes.set(true);
    this.utilityService.patchScheduleMetadata(this.utilityId, scheduleId, metadata).pipe(
      catchError((err) => {
        console.error('Error saving notes:', err);
        this.isSavingNotes.set(false);
        return of(null);
      })
    ).subscribe({
      next: (updated) => {
        this.isSavingNotes.set(false);
        if (updated) {
          this.scheduleMetadata.set(updated);
        }
      },
      error: () => {
        this.isSavingNotes.set(false);
      }
    });
  }
}
